import { runBoundedCompletion } from "../autonomy/bounded-completion.ts";
import type { CapabilityEnvelope, EvidenceBundle, EvidenceRef, GateOutcome } from "../autonomy/contracts.ts";
import {
	type EvidenceFindingDraft,
	normalizeEvidenceFinding,
	projectEvidenceFindings,
} from "../autonomy/evidence-finding-projection.ts";
import { parseModelOutputJsonObject } from "../model-output-json.ts";
import { RESEARCH_LANE_SYSTEM_PROMPT } from "../provider-prompt-contracts.ts";
import { createEvidenceBundle } from "./evidence-bundle.ts";
import { evaluateResearchRequest } from "./research-gate.ts";

export { RESEARCH_LANE_SYSTEM_PROMPT };

/**
 * Pure orchestration for one autonomous research pass: gate -> bounded isolated completion ->
 * parse -> evidence bundle. The model executor is injected so this stays provider-free and
 * session-free; production wires `AgentSession.runIsolatedCompletion` in.
 *
 * The lane is read-only by construction: the host may provide a classified read-only child tool
 * surface, and the output is an `EvidenceBundle` whose model-synthesized findings are untrusted.
 */

/** Static across calls so callers can use `cacheRetention: "short"` and only pay for the variable tail. */
export interface ResearchCompletion {
	text: string;
	costUsd: number;
	stopReason: string;
}

export interface ResearchRunnerOptions {
	query: string;
	/** Bounded, pre-redacted context handed to the research model (goal text, open requirements). */
	context?: string;
	/** Stripped research envelope - never the foreground/architect envelope. */
	envelope: CapabilityEnvelope;
	/**
	 * Pointer-first workspace sources (repo-relative path + bounded excerpt, never file bodies) that
	 * inform the pass. They are rendered into the user prompt and carried into the evidence bundle;
	 * omitted / empty reproduces the pre-collector behavior exactly.
	 */
	sources?: readonly EvidenceRef[];
	/** Budget for this pass; a post-hoc breach marks the run budget_exhausted (spend stays visible). */
	maxUsd: number;
	maxSources: number;
	maxFindings: number;
	/** Wall-clock budget in milliseconds; 0 disables. */
	maxWallClockMs: number;
	/** Executes one isolated completion. Production: AgentSession.runIsolatedCompletion. */
	complete: (args: { systemPrompt: string; userPrompt: string; signal?: AbortSignal }) => Promise<ResearchCompletion>;
	/** External cancellation (e.g. session disposal). */
	signal?: AbortSignal;
}

export type ResearchRunStatus = "succeeded" | "failed" | "canceled" | "timeout" | "budget_exhausted";

export interface ResearchRunResult {
	status: ResearchRunStatus;
	reasonCode: string;
	/** Underlying executor error for `completion_error` outcomes; absent for policy outcomes. */
	reasonDetail?: string;
	gateOutcome: GateOutcome;
	bundle?: EvidenceBundle;
	costUsd: number;
}

export function buildResearchUserPrompt(args: {
	query: string;
	context?: string;
	sources?: readonly EvidenceRef[];
	maxFindings: number;
}): string {
	const parts = [`QUERY ${args.query}`];
	if (args.context && args.context.length > 0) {
		parts.push("", "CONTEXT", args.context);
	}
	if (args.sources && args.sources.length > 0) {
		parts.push("", "WORKSPACE SOURCES; open files for full context");
		for (const source of args.sources) {
			const pointer = source.title ?? source.uri ?? source.id;
			parts.push(source.excerpt ? `- ${pointer}: ${source.excerpt}` : `- ${pointer}`);
		}
	}
	parts.push("", `MAX FINDINGS ${args.maxFindings}`);
	return parts.join("\n");
}

export interface ParsedResearchFindings {
	findings: EvidenceFindingDraft[];
}

export function parseResearchFindings(text: string, maxFindings: number): ParsedResearchFindings | undefined {
	const parsed = parseModelOutputJsonObject(text);
	const findingsRaw = parsed?.findings;
	if (!Array.isArray(findingsRaw)) return undefined;

	const findings: EvidenceFindingDraft[] = [];
	for (const item of findingsRaw) {
		const finding = normalizeEvidenceFinding(item);
		if (!finding) continue;
		findings.push(finding);
		if (findings.length >= maxFindings) break;
	}
	// A well-formed-but-empty findings array is a valid "nothing found"; a findings array whose
	// every item is malformed is not.
	return findings.length > 0 || findingsRaw.length === 0 ? { findings } : undefined;
}

function truncateExcerpt(text: string, maxChars: number): string {
	if (text.length <= maxChars) return text;
	return `${text.slice(0, Math.max(0, maxChars - 1))}…`;
}

function buildBundle(options: ResearchRunnerOptions, parsed: ParsedResearchFindings): EvidenceBundle {
	const contextRef: EvidenceRef = {
		id: "src-context",
		kind: "user",
		title: "Goal/context provided to the research lane",
		trusted: true,
		excerpt: truncateExcerpt(options.context && options.context.length > 0 ? options.context : options.query, 2000),
	};
	const synthesisRef: EvidenceRef = {
		id: "src-synthesis",
		kind: "tool",
		title: "Research-model synthesis",
		trusted: false,
	};
	// Context + synthesis are the fixed provenance anchors (findings cite src-synthesis); workspace
	// sources fill whatever budget is left between them. A one-source budget must reserve synthesis
	// because retaining context instead would leave generated findings with a dangling evidence ID.
	const budget = Math.max(1, options.maxSources);
	const workspaceRoom = Math.max(0, budget - 2);
	const workspaceSources = (options.sources ?? []).slice(0, workspaceRoom);
	const sources = budget === 1 ? [synthesisRef] : [contextRef, ...workspaceSources, synthesisRef];
	const findings = projectEvidenceFindings(parsed.findings, synthesisRef.id, options.maxFindings);
	return createEvidenceBundle({ query: options.query, sources, findings });
}

export async function runResearch(options: ResearchRunnerOptions): Promise<ResearchRunResult> {
	const gateOutcome = evaluateResearchRequest({
		envelope: options.envelope,
		sourceKind: "tool",
		estimatedUsd: options.maxUsd,
	});
	if (gateOutcome.outcome !== "allow") {
		// Skip-and-record, never prompt: gate denials inform diagnostics instead of blocking anything.
		const status: ResearchRunStatus = gateOutcome.reasonCode === "over_budget" ? "budget_exhausted" : "failed";
		return { status, reasonCode: gateOutcome.reasonCode, gateOutcome, costUsd: 0 };
	}

	const bounded = await runBoundedCompletion({
		maxWallClockMs: options.maxWallClockMs,
		signal: options.signal,
		execute: (signal) =>
			options.complete({
				systemPrompt: RESEARCH_LANE_SYSTEM_PROMPT,
				userPrompt: buildResearchUserPrompt(options),
				signal,
			}),
	});
	if (bounded.failure) {
		return {
			status: bounded.failure.status,
			reasonCode: bounded.failure.reasonCode,
			...(bounded.failure.detail ? { reasonDetail: bounded.failure.detail } : {}),
			gateOutcome,
			costUsd: bounded.completion?.costUsd ?? 0,
		};
	}
	const completion = bounded.completion;
	if (!completion) {
		return { status: "failed", reasonCode: "completion_error", gateOutcome, costUsd: 0 };
	}
	if (completion.stopReason === "error" || completion.stopReason === "aborted") {
		return { status: "failed", reasonCode: "model_error", gateOutcome, costUsd: completion.costUsd };
	}

	const parsed = parseResearchFindings(completion.text, options.maxFindings);
	if (!parsed) {
		return { status: "failed", reasonCode: "unparseable_output", gateOutcome, costUsd: completion.costUsd };
	}

	const bundle = buildBundle(options, parsed);
	const overBudget = options.maxUsd > 0 && completion.costUsd > options.maxUsd;
	return {
		status: overBudget ? "budget_exhausted" : "succeeded",
		reasonCode: overBudget
			? "cost_budget_exceeded"
			: parsed.findings.length === 0
				? "no_findings"
				: "research_completed",
		gateOutcome,
		bundle,
		costUsd: completion.costUsd,
	};
}
